import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import moment, { Moment } from 'moment';
import DateFormats from 'shared/constants/date-formats';

import { DateRange } from '../models/date-range';

export type DateRangePicker = {
  dateRangePickerIsOpen: boolean;
  startDate: Moment | undefined;
  endDate: Moment | undefined;
  temporaryStartDate: Moment | undefined;
  temporaryEndDate: Moment | undefined;
  maxEndDate: Moment | undefined;
  openDateRangePicker: () => void;
  closeDateRangePicker: () => void;
  applyStartDate: (startDate: Moment | undefined) => void;
  applyEndDate: (endDate: Moment | undefined) => void;
  applyTemporaryStartDate: (temporaryStartDate: Moment) => void;
  applyTemporaryEndDate: (temporaryEndDate: Moment) => void;
  apply: () => void;
};

const useDateRangePicker = (name: string, maxRangeInDays?: number): DateRangePicker => {
  const [dateRangePickerIsOpen, setDateRangePickerIsOpen] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Moment | undefined>();
  const [endDate, setEndDate] = useState<Moment | undefined>();
  const [temporaryStartDate, setTemporaryStartDate] = useState<Moment | undefined>();
  const [temporaryEndDate, setTemporaryEndDate] = useState<Moment | undefined>();
  const [maxEndDate, setMaxEndDate] = useState<Moment | undefined>();

  const { watch, getValues, setValue } = useFormContext();

  const fieldWatch = watch(name);

  const openDateRangePicker = (): void => {
    setTemporaryStartDate(startDate ?? moment());
    setTemporaryEndDate(endDate ?? moment());

    const newPossibleMaxEndDate = temporaryStartDate
      ? moment(temporaryStartDate).add(maxRangeInDays, 'days')
      : moment().add(maxRangeInDays, 'days');

    setMaxEndDate((maxEndDate ?? maxRangeInDays) ? newPossibleMaxEndDate : maxEndDate);
    setDateRangePickerIsOpen(true);
  };

  const closeDateRangePicker = (): void => {
    setDateRangePickerIsOpen(false);
    setMaxEndDate(undefined);
    setTemporaryStartDate(undefined);
  };

  const applyStartDate = (startDate: Moment | undefined): void => setStartDate(startDate);

  const applyEndDate = (endDate: Moment | undefined): void => setEndDate(endDate);

  const applyTemporaryStartDate = (temporaryStartDate: Moment): void => {
    setTemporaryStartDate(temporaryStartDate);
    setMaxEndDate(moment(temporaryStartDate).add(maxRangeInDays, 'days'));

    if (temporaryEndDate && temporaryEndDate < temporaryStartDate) {
      setTemporaryEndDate(temporaryStartDate);
      return;
    }

    if (maxRangeInDays && temporaryEndDate && temporaryStartDate && temporaryEndDate?.diff(temporaryStartDate, 'days') > 7) {
      setTemporaryEndDate(moment(temporaryStartDate).add(maxRangeInDays, 'days'));
    }
  };

  const applyTemporaryEndDate = (temporaryEndDate: Moment): void => {
    setTemporaryEndDate(temporaryEndDate);

    if (temporaryStartDate && temporaryStartDate > temporaryEndDate) {
      setTemporaryStartDate(temporaryEndDate);
      setMaxEndDate(moment(temporaryEndDate).add(maxRangeInDays, 'days'));
    }
  };

  const apply = (): void => {
    setValue(
      name,
      {
        startDate: moment(temporaryStartDate?.toDate()).format(DateFormats.ISO_8601_DATE_FORMAT),
        endDate: moment(temporaryEndDate?.toDate()).format(DateFormats.ISO_8601_DATE_FORMAT),
      } as DateRange,
      { shouldValidate: true, shouldDirty: true },
    );
    closeDateRangePicker();
  };

  // Sync form value to picker state
  useEffect(() => {
    const currentValue = getValues(name) as DateRange;

    if (currentValue) {
      applyStartDate(moment(currentValue.startDate));
      applyEndDate(moment(currentValue.endDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldWatch]);

  return {
    dateRangePickerIsOpen,
    startDate,
    endDate,
    temporaryStartDate,
    temporaryEndDate,
    maxEndDate,
    openDateRangePicker,
    closeDateRangePicker,
    applyStartDate,
    applyEndDate,
    applyTemporaryStartDate,
    applyTemporaryEndDate,
    apply,
  };
};

export default useDateRangePicker;
